/**
 * Returns (RMA) grid — shared grid for the customer-scoped plant surface
 * and the internal staff list page.
 *
 * The customer-facing term is "RMA" / "RMAs"; the internal code name is
 * "Return". The `variant` prop toggles the two modes:
 *   - "customer": scoped to a single SiteGroup; shows Site column; row
 *     clicks navigate within the plant context URL.
 *   - "internal": unscoped; adds the SiteGroup column; leads with the
 *     customer/group context pickers (the page's single scope surface,
 *     which the typed scope search also fills), then stat cards and the
 *     status / workflow / date-range toolbar filters for staff.
 *
 * Server-backed via EntityGrid over the odl-app rmas/ surface: columns,
 * order, sizing, and default sort (-update_time) come from the grid-spec
 * descriptor; this file supplies render overrides + the caller-owned
 * filter bar. Filter values feed extraParams using the descriptor's
 * FilterField names (customer_id, site_group_id, status, is_quick_ship,
 * needs_photo, ready_to_invoice, delinquent_quick_ship) plus the datetime
 * range fields in the dot-suffix form the odl-app filter layer parses
 * (input_time.gte/.lte, shipping_date, received_date, invoiced_time,
 * update_time); the search box reshapes to filter[search].
 *
 * Contains:
 * - ReturnGrid — the default export (both variants)
 */
import BoltIcon from '@mui/icons-material/Bolt';
import CircleIcon from '@mui/icons-material/Circle';
import EventOutlined from '@mui/icons-material/EventOutlined';
import FlagOutlined from '@mui/icons-material/FlagOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { green } from '@mui/material/colors';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { addDays, format } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { fetchRmaStats, postRmaBatch } from '@sophocles/api/rma';
import { useUser } from '@sophocles/app';
import FilterBar from '@sophocles/components/FilterBar';
import ScopeSearchInput from '@sophocles/components/ScopeSearchInput';
import ScopeContextPickers from '@sophocles/components/ScopeSearchInput/ScopeContextPickers';
import { customerScopeProvider, siteGroupScopeProvider } from '@sophocles/components/ScopeSearchInput/scopeProviders';
import { API_DOMAIN } from '@sophocles/config';
import { EntityGrid } from '@sophocles/data-grids';
import GridLinkCell from '@sophocles/data-grids/components/cells/GridLinkCell';
import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import { toJsonApiParams } from '@sophocles/data-grids/datasource/params';
import useGridPreferenceKey, { useGridSessionKey } from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import useGridQuickFilter from '@sophocles/data-grids/datasource/useGridQuickFilter';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import { refLabel } from '@sophocles/features/entities/references';
import { addToast } from '@sophocles/features/toast';
import usePersistedState from '@sophocles/hooks/usePersistedState';
import useUserPreference from '@sophocles/hooks/useUserPreference';
import { calculatePresetRange, getDefaultDateRangePresets } from '@sophocles/utils/dateRangePresets';

import { buildStatusOptions, getStatusLabel, rmaStatusFamily } from '../utils/rmaStatus';

import ReturnStatCards from './ReturnStatCards';

// Descriptor columns the legacy grid never surfaced (issue, the workflow
// booleans) start hidden for parity; the old-device ICCID is surfaced.
// Users can still toggle any of these via the grid settings menu.
const PARITY_COLUMN_VISIBILITY = {
  issue: false,
  is_quick_ship: false,
  needs_photo: false,
  ready_to_invoice: false,
  legacy_incoming_iccid: true,
};

// Date Issued relative-range presets (single-select chip beside Status;
// null value = "All time"). The six keys below are the subset of
// RelativeDateRangePicker's default presets this filter surfaces.
const DATE_ISSUED_PRESET_KEYS = ['today', 'last7days', 'last30days', 'last90days', 'last6months', 'last12months'];

/**
 * Convert a Date (or persisted ISO string) to yyyy-mm-dd for API params.
 * Returns undefined for empty/invalid input so the param is omitted.
 */
function toIsoDate(date) {
  if (!date) return undefined;
  const d = date instanceof Date ? date : new Date(date);
  return Number.isNaN(d.getTime()) ? undefined : format(d, 'yyyy-MM-dd');
}

/**
 * Resolve a Date Issued preset key to { gte, lte } ISO bounds. null/unknown
 * (i.e. "All time") yields {} so no input_time bounds are applied.
 */
function presetToRange(preset, presetOptions) {
  const option = presetOptions.find(p => p.value === preset);
  if (!option) return {};
  const { start } = calculatePresetRange(option);
  // input_time is a datetime, so a bare date `lte` resolves to that day's
  // midnight and would exclude everything later today; bound it to tomorrow's
  // date so the window is inclusive of all of today's records.
  return { gte: toIsoDate(start), lte: toIsoDate(addDays(new Date(), 1)) };
}

/**
 * Revive a persisted { from, to } range — sessionStorage round-trips Dates
 * to ISO strings — back into Date objects for the FilterBar daterange value.
 */
function reviveRange(range) {
  if (!range) return null;
  return {
    from: range.from ? new Date(range.from) : null,
    to: range.to ? new Date(range.to) : null,
  };
}

function oldDevice(row) {
  return row?.radio_returned_by_user?.iccid || row?.legacy_incoming_iccid || '';
}

// A quick-ship RMA still in Issued status is effectively "Quick-Ship Pending"
// — surface it as the warning (orange) state rather than plain Issued (blue).
function isQuickShipPending(row) {
  return row?.status === 0 && Boolean(row?.is_quick_ship);
}

// Status family for a row's effective status — quick-ship pending overrides
// plain Issued. Drives the pill and the dot beside the RMA # alike.
function statusFamily(row) {
  if (isQuickShipPending(row)) return 'warning';
  return rmaStatusFamily(row?.status);
}

// sx color for the status dot beside the RMA # — the family's base token.
function statusDotColor(row) {
  return `var(--status-${statusFamily(row)})`;
}

// Tooltip label for the status dot — reflects the effective state.
function statusDotLabel(t, row) {
  if (isQuickShipPending(row)) return t('rma.quickShipPending', 'Quick-Ship Pending');
  return getStatusLabel(t, row?.status);
}

// Action buttons per row — same set for both variants, already role-gated
// by the permission booleans so non-staff customers only see their own actions.
function RowActions({ row, isStaff, isSupport, isShipping, isAccounting, navigate, t }) {
  const rmaUrl = `/rma/${row.id}/`;
  const status = row.status ?? -1;
  const isQuickShip = row.is_quick_ship ?? false;
  const readyToInvoice = row.ready_to_invoice ?? false;
  const needsPhoto = row.needs_photo ?? false;
  const hiddenComments = row.hidden_comments ?? '';
  const hasHiddenComments = isStaff && hiddenComments && hiddenComments.length > 0;

  // Guards mirror legacy RmaListTable.js exactly so behavior stays
  // in lock-step until the legacy table is removed.
  const showEdit = status == 0 && isSupport;
  const showQuickShip = status == 0 && isQuickShip && isShipping;
  const showProcessIssued = status == 0 && !isQuickShip && isShipping;
  const showProcessQuickShipped = status == 2 && isShipping;
  const showAddPicture = needsPhoto;
  const showInvoice = (status == 1 || readyToInvoice) && isAccounting;
  // Result summary exists once the RMA is processed (or complete) — the same
  // states the detail page's print menu offers it for.
  const showPrint = (status == 1 || status == 100) && isStaff;

  return (
    <Stack direction="row" spacing={0.5} onClick={e => e.stopPropagation()}>
      {showEdit && (
        <Button
          onClick={() => navigate(`${rmaUrl}issue`)}
          variant="contained"
          size="small"
          sx={{ textTransform: 'none' }}
        >
          {t('actions.edit', 'Edit')}
        </Button>
      )}
      {showQuickShip && (
        <Button
          onClick={() => navigate(`${rmaUrl}quickship`)}
          variant="contained"
          size="small"
          sx={{ textTransform: 'none' }}
        >
          {t('general.quickShip', 'Quick Ship')}
        </Button>
      )}
      {(showProcessIssued || showProcessQuickShipped) && (
        <Button
          onClick={() => navigate(`${rmaUrl}process`)}
          variant="contained"
          size="small"
          sx={{ textTransform: 'none' }}
        >
          {t('rma.process', 'Process')}
        </Button>
      )}
      {showAddPicture && (
        <Button
          onClick={() => navigate(`${rmaUrl}process`)}
          variant="contained"
          size="small"
          sx={{ textTransform: 'none' }}
        >
          {t('rma.addPicture', 'Add Picture')}
        </Button>
      )}
      {showInvoice && (
        <Button
          onClick={() => navigate(`${rmaUrl}invoice`)}
          variant="contained"
          size="small"
          sx={{ textTransform: 'none' }}
        >
          {t('rma.invoice', 'Invoice')}
        </Button>
      )}
      {showPrint && (
        <Tooltip title={t('rma.printResultSummary', 'Print Result Summary')}>
          <IconButton
            size="small"
            aria-label={t('rma.printResultSummary', 'Print Result Summary')}
            onClick={() => navigate(`${rmaUrl}resultsummary`)}
          >
            <PrintOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {hasHiddenComments && (
        <Tooltip title={hiddenComments}>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', alignSelf: 'center', ml: 0.5, cursor: 'default' }}
          >
            {t('rma.hiddenComments', 'Note')}
          </Typography>
        </Tooltip>
      )}
    </Stack>
  );
}

// Mobile single-column summary card, shared by both variants.
function MobileSummaryCell({ row, linkTo, showSiteGroup, customerTerm, siteGroupTerm, t }) {
  return (
    <Box
      component={Link}
      to={linkTo}
      sx={{ display: 'block', textDecoration: 'none', color: 'inherit', py: 1, px: 0.5, width: '100%', minWidth: 0 }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
        <Typography sx={{ fontWeight: 600, color: 'primary.main', flexShrink: 0 }}>{row.rma_number}</Typography>
        {row.is_quick_ship && (
          <Tooltip title={t('general.quickShip', 'Quick Ship')}>
            <BoltIcon sx={{ fontSize: '1rem', color: green[500], flexShrink: 0 }} />
          </Tooltip>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <GridStatusCell label={statusDotLabel(t, row)} color={statusFamily(row)} sx={{ flexShrink: 0 }} />
      </Stack>
      {showSiteGroup && row.site_group && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
          {customerTerm}: {row.site_group?.customer?.name ?? ''}
          {' · '}
          {siteGroupTerm}: {refLabel(row.site_group)}
        </Typography>
      )}
      {row.site?.name && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
          {row.site.name}
        </Typography>
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', fontFamily: 'monospace', overflowWrap: 'anywhere' }}
      >
        {oldDevice(row)}
      </Typography>
    </Box>
  );
}

/**
 * ReturnGrid — EntityGrid over the odl-app rmas/ surface (both variants).
 *
 * Props:
 *   variant     – 'customer' | 'internal'
 *   customer    – Immutable Map (customer variant: the scoped customer)
 *   siteGroupId – number (customer variant: the scoped site group's id)
 */
export default function ReturnGrid({ variant = 'customer', customer, siteGroupId }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isStaff, permissions } = useUser();
  const { isTechSupport: isSupport, isShipping, isAccounting } = permissions;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isCustomer = variant === 'customer';

  // Tenant terminology via i18n (the app convention — e.g. "Group", not the
  // literal "Site Group"). Billboard customers see "Plant" for a site group;
  // the staff (cross-customer) grid uses the generic group term.
  const customerTerm = t('general.customer', 'Customer');
  const isBillboardCustomer = isCustomer ? Boolean(customer?.get?.('is_billboard', true)) : false;
  const siteGroupTerm = isBillboardCustomer ? t('general.plant', 'Plant') : t('general.siteGroup', 'Site Group');
  const persistKey = isCustomer ? 'rmaGrid' : 'internalRmaGrid';

  // Customer variant: customerId still comes off the Immutable customer prop;
  // siteGroupId arrives as a plain id — the parent resolves it from its own
  // Immutable siteGroup entity at its own boundary.
  const customerId = isCustomer ? customer?.get('id') : null;

  // Internal variant scope filters — persisted across sessions. The customer
  // chip scopes by customer_id; the site-group chip nests under it.
  const [customerFilter, setCustomerFilter] = usePersistedState(useGridSessionKey(persistKey, 'customer'), null);
  const [siteGroupFilter, setSiteGroupFilter] = usePersistedState(useGridSessionKey(persistKey, 'siteGroup'), null);
  const [scopeLabels, setScopeLabels] = useState({ customer: null, site_group: null });

  // Internal variant workflow filters — the single source of truth shared by
  // the stat cards AND the filter menu. A stat card is a shortcut that sets
  // this same state, so cards and menu controls compose and each filter shows
  // exactly one chip (the menu's), never a duplicate.
  const [statusFilter, setStatusFilter] = usePersistedState(useGridSessionKey(persistKey, 'status'), null);
  const [quickShip, setQuickShip] = usePersistedState(useGridSessionKey(persistKey, 'quickShip'), null); // 'yes' | 'no'
  const [needsPhoto, setNeedsPhoto] = usePersistedState(useGridSessionKey(persistKey, 'needsPhoto'), null); // 'true'
  const [readyToInvoice, setReadyToInvoice] = usePersistedState(useGridSessionKey(persistKey, 'readyToInvoice'), null); // 'true'
  const [delinquent, setDelinquent] = usePersistedState(useGridSessionKey(persistKey, 'delinquent'), null); // 'true'

  // Internal variant: Date Issued relative-range preset (single-select chip
  // beside Status). null = "All time" (no input_time bounds).
  const [dateIssuedPreset, setDateIssuedPreset] = usePersistedState(
    useGridSessionKey(persistKey, 'dateIssuedPreset'),
    null,
  );

  // Date Issued preset options — the RelativeDateRangePicker default presets,
  // narrowed to the keys this filter surfaces.
  const dateIssuedPresetOptions = useMemo(
    () => getDefaultDateRangePresets(t).filter(preset => DATE_ISSUED_PRESET_KEYS.includes(preset.value)),
    [t],
  );

  // Internal variant: moreFilters date ranges ({ from: Date|null, to: Date|null }).
  const [dateShipped, setDateShipped] = usePersistedState(useGridSessionKey(persistKey, 'dateShipped'), null);
  const [dateProcessed, setDateProcessed] = usePersistedState(useGridSessionKey(persistKey, 'dateProcessed'), null);
  const [dateInvoiced, setDateInvoiced] = usePersistedState(useGridSessionKey(persistKey, 'dateInvoiced'), null);
  const [lastUpdated, setLastUpdated] = usePersistedState(useGridSessionKey(persistKey, 'lastUpdated'), null);

  // Own the grid apiRef so the internal ScopeSearchInput can drive the quick
  // filter while EntityGrid manages the grid. EntityGrid mounts only after the
  // spec resolves, so gridReady re-triggers the quick-filter sync on cold load.
  const apiRef = useGridApiRef();
  const [gridReady, setGridReady] = useState(false);
  const [searchText, setSearchText, commitSearch, committedSearch] = useGridQuickFilter(apiRef, gridReady);

  // Summary state for the stat card row (odl-app rmas/stats/).
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // extraParams carries the odl-app grid-spec FilterField names. The odl-app
  // serializer wraps every key as filter[<key>], and the BE filter layer parses
  // the dot-suffix range form (e.g. input_time.gte → filter[input_time.gte]),
  // so date bounds set keys with the operator inside the key. Must be memoized
  // (a fresh object recreates the dataSource → refetch loop, worse under
  // infinite scroll).
  const extraParams = useMemo(() => {
    if (isCustomer) {
      return siteGroupId ? { site_group_id: siteGroupId } : {};
    }

    const params = {};
    if (customerFilter) params.customer_id = customerFilter;
    if (siteGroupFilter) params.site_group_id = siteGroupFilter;

    // Date Issued bounds (from the relative-range preset).
    const issuedRange = presetToRange(dateIssuedPreset, dateIssuedPresetOptions);
    if (issuedRange.gte) params['input_time.gte'] = issuedRange.gte;
    if (issuedRange.lte) params['input_time.lte'] = issuedRange.lte;

    // moreFilters date ranges — value is { from: Date|null, to: Date|null }.
    if (dateShipped?.from) params['shipping_date.gte'] = toIsoDate(dateShipped.from);
    if (dateShipped?.to) params['shipping_date.lte'] = toIsoDate(dateShipped.to);
    if (dateProcessed?.from) params['received_date.gte'] = toIsoDate(dateProcessed.from);
    if (dateProcessed?.to) params['received_date.lte'] = toIsoDate(dateProcessed.to);
    if (dateInvoiced?.from) params['invoiced_time.gte'] = toIsoDate(dateInvoiced.from);
    if (dateInvoiced?.to) params['invoiced_time.lte'] = toIsoDate(dateInvoiced.to);
    if (lastUpdated?.from) params['update_time.gte'] = toIsoDate(lastUpdated.from);
    if (lastUpdated?.to) params['update_time.lte'] = toIsoDate(lastUpdated.to);

    // Workflow filters (shared by the stat cards and the filter menu).
    if (statusFilter != null) params.status = statusFilter;
    if (quickShip === 'yes') params.is_quick_ship = true;
    else if (quickShip === 'no') params.is_quick_ship = false;
    if (needsPhoto === 'true') params.needs_photo = true;
    if (readyToInvoice === 'true') params.ready_to_invoice = true;
    if (delinquent === 'true') params.delinquent_quick_ship = true;
    return params;
  }, [
    isCustomer,
    siteGroupId,
    customerFilter,
    siteGroupFilter,
    dateIssuedPreset,
    dateIssuedPresetOptions,
    dateShipped,
    dateProcessed,
    dateInvoiced,
    lastUpdated,
    statusFilter,
    quickShip,
    needsPhoto,
    readyToInvoice,
    delinquent,
  ]);

  // Summary counts take the same filter[...] params the grid list uses —
  // scope, date ranges, and the committed search — so the cards follow the
  // list the user is looking at. Bucket-selection keys (status, quick-ship,
  // workflow booleans) ride along untouched; the stats endpoint strips them
  // so each bucket counts independently.
  const summaryParams = useMemo(() => {
    if (isCustomer) return null;
    const params = { ...extraParams };
    if (committedSearch) params.search = committedSearch;
    return toJsonApiParams(params);
  }, [isCustomer, extraParams, committedSearch]);

  // Bumped after a bulk invoice so the cards recount without a param change.
  const [summaryReloadToken, setSummaryReloadToken] = useState(0);
  useEffect(() => {
    if (!summaryParams) return;
    let cancelled = false;
    setSummaryLoading(true);
    fetchRmaStats(summaryParams)
      .then(data => {
        if (!cancelled) setSummary(data);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [summaryParams, summaryReloadToken]);

  // --- Bulk selection actions (internal variant): print + invoice ---
  // The grid runs infinite scroll, so selection ids are grid row ids —
  // resolve each through the grid api before using them as RMA pks.
  const { onGridReady: captureGridReady, refresh: refreshGrid } = useGridRefreshRef();
  // { okIds, blocked: [{id, detail}], clear } — set by the preflight, drives
  // the confirm dialog; null when no bulk invoice is in flight.
  const [bulkInvoice, setBulkInvoice] = useState(null);
  const [bulkInvoiceRunning, setBulkInvoiceRunning] = useState(false);

  const handleBulkInvoiceRequest = useCallback(
    async (rmaIds, clear) => {
      const commands = rmaIds.map(id => ({ key: 'invoice', params: { id } }));
      try {
        const entries = await postRmaBatch({ commands, preflight: true });
        setBulkInvoice({
          okIds: entries.filter(entry => entry.status === 'ok').map(entry => entry.id),
          blocked: entries
            .filter(entry => entry.status !== 'ok')
            .map(entry => ({ id: rmaIds[entry.index], detail: entry.detail })),
          clear,
        });
      } catch {
        dispatch(
          addToast({
            title: t('rma.invoiceSelected', 'Invoice Selected'),
            message: t('rma.bulkPreflightFailed', 'Could not check the selected RMAs. Try again.'),
            level: 'error',
            dismissible: true,
          }),
        );
      }
    },
    [dispatch, t],
  );

  const handleBulkInvoiceConfirm = useCallback(async () => {
    if (!bulkInvoice?.okIds?.length) {
      setBulkInvoice(null);
      return;
    }
    setBulkInvoiceRunning(true);
    try {
      const commands = bulkInvoice.okIds.map(id => ({ key: 'invoice', params: { id } }));
      const entries = await postRmaBatch({ commands });
      const okCount = entries.filter(entry => entry.status === 'ok').length;
      const failCount = entries.length - okCount;
      dispatch(
        addToast({
          title: t('rma.invoiceSelected', 'Invoice Selected'),
          message: failCount
            ? t('rma.bulkInvoiceMixed', '{{ok}} invoiced, {{failed}} failed.', { ok: okCount, failed: failCount })
            : t('rma.bulkInvoiceDone', '{{ok}} RMAs invoiced.', { ok: okCount }),
          level: failCount ? 'warning' : 'success',
          dismissible: true,
        }),
      );
      bulkInvoice.clear?.();
      refreshGrid();
      setSummaryReloadToken(token => token + 1);
    } catch {
      dispatch(
        addToast({
          title: t('rma.invoiceSelected', 'Invoice Selected'),
          message: t('rma.bulkInvoiceFailed', 'Bulk invoice failed. Try again.'),
          level: 'error',
          dismissible: true,
        }),
      );
    } finally {
      setBulkInvoiceRunning(false);
      setBulkInvoice(null);
    }
  }, [bulkInvoice, dispatch, t, refreshGrid]);

  const selectionActions = useCallback(
    (selection, clearSelection, selectionApiRef) => {
      const rows = (selection.ids ?? []).map(id => selectionApiRef.current?.getRow?.(id)).filter(Boolean);
      const rmaIds = rows.map(row => row.id);
      if (!rmaIds.length) return null;
      const actionSx = { color: 'inherit', textTransform: 'none', fontWeight: 600 };
      // Client-side readiness gate ahead of the server preflight: the rows
      // carry the backend-computed ready_to_invoice flag, so a selection
      // containing any not-ready RMA — including a bare select-all without
      // the Ready to Invoice filter narrowing the grid — disables the
      // action rather than offering a batch that would partly skip.
      const notReadyCount = isAccounting ? rows.filter(row => !row.ready_to_invoice).length : 0;
      return (
        <>
          <Button
            size="small"
            startIcon={<PrintOutlinedIcon />}
            onClick={() => navigate(`/rmas/print?ids=${rmaIds.join(',')}`)}
            sx={actionSx}
          >
            {t('rma.printSelected', 'Print Selected')}
          </Button>
          {isAccounting && (
            <Tooltip
              title={
                notReadyCount
                  ? t(
                      'rma.invoiceSelectedNotReady',
                      '{{count}} selected RMAs are not ready to invoice — apply the Ready to Invoice filter or deselect them.',
                      { count: notReadyCount },
                    )
                  : ''
              }
            >
              <span>
                <Button
                  size="small"
                  startIcon={<ReceiptLongOutlinedIcon />}
                  disabled={notReadyCount > 0}
                  onClick={() => handleBulkInvoiceRequest(rmaIds, clearSelection)}
                  sx={actionSx}
                >
                  {t('rma.invoiceSelected', 'Invoice Selected')}
                </Button>
              </span>
            </Tooltip>
          )}
        </>
      );
    },
    [navigate, t, isAccounting, handleBulkInvoiceRequest],
  );

  // Customer variant navigates within the plant context.
  // Internal variant navigates to the staff RMA view.
  const getViewUrl = useCallback(
    rmaId => (isCustomer ? `/c/${customerId}/plant/${siteGroupId}/support/rma/${rmaId}/view` : `/rma/${rmaId}/view`),
    [isCustomer, customerId, siteGroupId],
  );

  // --- Column visibility (controlled; hides descriptor extras for parity) ---
  const [columnVisibilityPrefs, setColumnVisibilityPrefs] = useUserPreference(
    useGridPreferenceKey(persistKey, 'columnVisibility.v2'),
    {},
  );

  const columnVisibilityModel = useMemo(
    () => ({
      ...PARITY_COLUMN_VISIBILITY,
      // Customer variant is already scoped to one customer and plant — the
      // customer and site-group columns are redundant there (legacy customer
      // grid never showed them).
      ...(isCustomer ? { customer: false, site_group: false } : null),
      ...columnVisibilityPrefs,
    }),
    [isCustomer, columnVisibilityPrefs],
  );

  const columnVisibility = useMemo(
    () => ({ model: columnVisibilityModel, onChange: setColumnVisibilityPrefs }),
    [columnVisibilityModel, setColumnVisibilityPrefs],
  );

  // --- fieldOverrides (render-only; sizing/order/headers come from descriptor) ---
  const fieldOverrides = useMemo(
    () => ({
      rma_number: {
        cellClassName: 'cell-pinned',
        display: 'flex',
        renderCell: ({ row, value }) => (
          <GridLinkCell
            to={getViewUrl(row.id)}
            sx={{
              color: 'primary.main',
              // Global a:visited rule (styles.scss) recolors visited links;
              // pin it so the id link stays primary after navigation.
              '&:visited': { color: 'primary.main' },
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <Tooltip title={statusDotLabel(t, row)}>
              <CircleIcon sx={{ mr: 0.75, fontSize: '0.7rem', color: statusDotColor(row) }} />
            </Tooltip>
            {value}
            {row.is_quick_ship && (
              <Tooltip title={t('general.quickShip', 'Quick Ship')}>
                <BoltIcon sx={{ ml: 0.5, fontSize: '1rem', color: green[500] }} />
              </Tooltip>
            )}
          </GridLinkCell>
        ),
      },
      update_time: {
        display: 'flex',
        renderCell: ({ value }) =>
          value ? (
            <Tooltip title={new Date(value).toLocaleString()}>
              <Typography variant="body2">{new Date(value).toLocaleDateString()}</Typography>
            </Tooltip>
          ) : (
            ''
          ),
      },
      // customer / site / site_group ride a {type,id,name} ref payload. Render
      // plain text from the row (a renderCell override, NOT valueGetter, so it
      // wins over the descriptor's reference-chip renderer either way).
      customer: {
        renderCell: ({ row }) => row?.customer?.name ?? '',
      },
      site: {
        renderCell: ({ row }) => row?.site?.name ?? '',
      },
      site_group: {
        renderCell: ({ row }) => refLabel(row?.site_group),
      },
      status: {
        renderCell: ({ row }) => {
          const label = row?.status_display;
          if (!label) return null;
          return <GridStatusCell label={label} color={rmaStatusFamily(row?.status)} />;
        },
      },
      // Row payload omits the returned radio (radios are dropped from the grid
      // row by design), so this degrades to the incoming ICCID only.
      legacy_incoming_iccid: {
        headerName: t('rma.oldDevice', 'Old Device'),
      },
    }),
    [t, getViewUrl],
  );

  // --- Bespoke trailing actions column (descriptor can't model it) ---
  const actionsColumn = useMemo(
    () => ({
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      minWidth: 360,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      display: 'flex',
      renderCell: params => (
        <RowActions
          row={params.row}
          isStaff={isStaff}
          isSupport={isSupport}
          isShipping={isShipping}
          isAccounting={isAccounting}
          navigate={navigate}
          t={t}
        />
      ),
    }),
    [isStaff, isSupport, isShipping, isAccounting, navigate, t],
  );

  // --- Internal scope search (customer + nested site-group chips) ---
  // One pair of scope handlers serves both apply surfaces — the typed
  // scope-search suggestions and the More-filters pick-lists — so either
  // path yields the identical chip and state.
  const handleScopeCustomerChange = useCallback(
    selection => {
      setCustomerFilter(selection?.id ?? null);
      setSiteGroupFilter(null);
      setScopeLabels({ customer: selection?.label ?? null, site_group: null });
    },
    [setCustomerFilter, setSiteGroupFilter],
  );
  const handleScopeSiteGroupChange = useCallback(
    selection => {
      setSiteGroupFilter(selection?.id ?? null);
      setScopeLabels(prev => ({ ...prev, site_group: selection?.label ?? null }));
    },
    [setSiteGroupFilter],
  );

  const scopeProviders = useMemo(() => {
    if (isCustomer) return [];
    return [
      customerScopeProvider({
        label: customerTerm,
        isActive: () => Boolean(customerFilter),
        applyValue: handleScopeCustomerChange,
      }),
      siteGroupScopeProvider({
        label: siteGroupTerm,
        getCustomerId: () => customerFilter,
        isActive: () => Boolean(siteGroupFilter),
        applyValue: handleScopeSiteGroupChange,
      }),
    ];
  }, [
    isCustomer,
    customerFilter,
    siteGroupFilter,
    handleScopeCustomerChange,
    handleScopeSiteGroupChange,
    customerTerm,
    siteGroupTerm,
  ]);

  // No scope chips here: the page-level context pickers above the stat cards
  // are the single display of the applied customer/group. The typed scope
  // providers stay — picking a suggestion runs the same handlers and fills
  // the pickers.
  const scopeSearch = !isCustomer ? (
    <ScopeSearchInput
      value={searchText}
      onValueChange={setSearchText}
      providers={scopeProviders}
      placeholder={t('general.searchRMAs', 'Search RMAs')}
      onSubmit={commitSearch}
    />
  ) : undefined;

  // Inline quick filters: Status (chip with colored status options) and Date
  // Issued (relative-range preset chip; null = All time → no input_time bounds).
  const quickFilters = useMemo(() => {
    if (isCustomer) return [];
    return [
      {
        key: 'status',
        label: t('general.status', 'Status'),
        type: 'single',
        allLabel: t('general.all', 'All'),
        icon: <FlagOutlined sx={{ fontSize: 16 }} />,
        options: buildStatusOptions(t),
        value: statusFilter,
        onChange: value => setStatusFilter(value),
        renderOption: opt => <GridStatusCell label={opt.label} color={rmaStatusFamily(Number(opt.value))} />,
      },
      {
        key: 'dateIssued',
        label: t('rma.dateIssued', 'Date Issued'),
        type: 'single',
        allLabel: t('dateRange.allTime', 'All time'),
        icon: <EventOutlined sx={{ fontSize: 16 }} />,
        options: dateIssuedPresetOptions,
        value: dateIssuedPreset,
        onChange: value => setDateIssuedPreset(value),
      },
    ];
  }, [isCustomer, statusFilter, setStatusFilter, dateIssuedPreset, setDateIssuedPreset, dateIssuedPresetOptions, t]);

  // Filter-icon popover: the workflow filters (shared with the stat cards) plus
  // the Delinquent toggle and the four date ranges. Selecting a card sets these
  // same values, so each shows a single chip whether driven from a card or menu.
  const moreFilters = useMemo(() => {
    if (isCustomer) return [];
    return [
      {
        key: 'quickShip',
        label: t('general.quickShip', 'Quick Ship'),
        type: 'single',
        allLabel: t('general.any', 'Any'),
        options: [
          { value: 'yes', label: t('general.yes', 'Yes') },
          { value: 'no', label: t('general.no', 'No') },
        ],
        value: quickShip,
        onChange: value => setQuickShip(value),
      },
      {
        key: 'readyToInvoice',
        label: t('rma.readyToInvoice', 'Ready to Invoice'),
        type: 'boolean',
        allLabel: t('general.any', 'Any'),
        options: [{ value: 'true', label: t('general.yes', 'Yes') }],
        value: readyToInvoice,
        onChange: value => setReadyToInvoice(value),
      },
      {
        key: 'needsPhoto',
        label: t('rma.needsPhoto', 'Needs Photo'),
        type: 'boolean',
        allLabel: t('general.any', 'Any'),
        options: [{ value: 'true', label: t('general.yes', 'Yes') }],
        value: needsPhoto,
        onChange: value => setNeedsPhoto(value),
      },
      {
        key: 'delinquent',
        label: t('rma.delinquent', 'Delinquent'),
        type: 'boolean',
        allLabel: t('general.any', 'Any'),
        options: [{ value: 'true', label: t('general.yes', 'Yes') }],
        value: delinquent,
        onChange: value => setDelinquent(value),
      },
      {
        key: 'dateShipped',
        label: t('rma.dateShipped', 'Date Shipped'),
        type: 'daterange',
        value: reviveRange(dateShipped),
        onChange: value => setDateShipped(value),
      },
      {
        key: 'dateProcessed',
        label: t('rma.dateProcessed', 'Date Processed'),
        type: 'daterange',
        value: reviveRange(dateProcessed),
        onChange: value => setDateProcessed(value),
      },
      {
        key: 'dateInvoiced',
        label: t('rma.dateInvoiced', 'Date Invoiced'),
        type: 'daterange',
        value: reviveRange(dateInvoiced),
        onChange: value => setDateInvoiced(value),
      },
      {
        key: 'lastUpdated',
        label: t('rma.lastUpdated', 'Last Updated'),
        type: 'daterange',
        value: reviveRange(lastUpdated),
        onChange: value => setLastUpdated(value),
      },
    ];
  }, [
    isCustomer,
    quickShip,
    readyToInvoice,
    needsPhoto,
    delinquent,
    dateShipped,
    dateProcessed,
    dateInvoiced,
    lastUpdated,
    setQuickShip,
    setReadyToInvoice,
    setNeedsPhoto,
    setDelinquent,
    setDateShipped,
    setDateProcessed,
    setDateInvoiced,
    setLastUpdated,
    t,
  ]);

  // Clears every workflow filter (shared by "All" card + Clear all).
  const clearWorkflowFilters = useCallback(() => {
    setStatusFilter(null);
    setQuickShip(null);
    setNeedsPhoto(null);
    setReadyToInvoice(null);
    setDelinquent(null);
  }, [setStatusFilter, setQuickShip, setNeedsPhoto, setReadyToInvoice, setDelinquent]);

  const handleResetAll = useCallback(() => {
    clearWorkflowFilters();
    setCustomerFilter(null);
    setSiteGroupFilter(null);
    setScopeLabels({ customer: null, site_group: null });
    setDateIssuedPreset(null);
    setDateShipped(null);
    setDateProcessed(null);
    setDateInvoiced(null);
    setLastUpdated(null);
  }, [
    clearWorkflowFilters,
    setCustomerFilter,
    setSiteGroupFilter,
    setDateIssuedPreset,
    setDateShipped,
    setDateProcessed,
    setDateInvoiced,
    setLastUpdated,
  ]);

  // A stat card is a shortcut that sets the shared workflow-filter state. The
  // active card is derived from that state (not a separate selection), so the
  // cards, the Status chip, and the menu filters always agree.
  const activeBucket = useMemo(() => {
    const onlyStatusQuickShip = !needsPhoto && !readyToInvoice && !delinquent;
    if (statusFilter === '0' && quickShip === 'no' && onlyStatusQuickShip) return 'awaiting_processing';
    if (statusFilter === '0' && quickShip === 'yes' && onlyStatusQuickShip) return 'quickship_pending';
    if (needsPhoto === 'true' && statusFilter == null && !quickShip && !readyToInvoice && !delinquent)
      return 'needs_photo';
    if (readyToInvoice === 'true' && statusFilter == null && !quickShip && !needsPhoto && !delinquent)
      return 'billing_needed';
    if (delinquent === 'true' && statusFilter == null && !quickShip && !needsPhoto && !readyToInvoice)
      return 'delinquent';
    if (statusFilter == null && !quickShip && !needsPhoto && !readyToInvoice && !delinquent) return 'all';
    return null; // a custom combination — no single card represents it
  }, [statusFilter, quickShip, needsPhoto, readyToInvoice, delinquent]);

  const handleBucketSelect = useCallback(
    bucketKey => {
      // null = toggle-off / "All" → clear the workflow dimensions.
      clearWorkflowFilters();
      switch (bucketKey) {
        case 'awaiting_processing':
          setStatusFilter('0');
          setQuickShip('no');
          break;
        case 'quickship_pending':
          setStatusFilter('0');
          setQuickShip('yes');
          break;
        case 'needs_photo':
          setNeedsPhoto('true');
          break;
        case 'billing_needed':
          setReadyToInvoice('true');
          break;
        case 'delinquent':
          setDelinquent('true');
          break;
        default:
          break; // null / 'all' — already cleared
      }
    },
    [clearWorkflowFilters, setStatusFilter, setQuickShip, setNeedsPhoto, setReadyToInvoice, setDelinquent],
  );

  const toolbarContent = !isCustomer ? (
    <FilterBar quickFilters={quickFilters} moreFilters={moreFilters} onResetAll={handleResetAll} />
  ) : undefined;

  // Context pickers lead the header: pick the customer, then their group, and
  // the whole page — grid, cards, New RMA — works within that context. The
  // typed scope search writes the same state, so a suggestion picked there
  // fills these pickers.
  const statsHeader = !isCustomer ? (
    <>
      <ScopeContextPickers
        customerTerm={customerTerm}
        siteGroupTerm={siteGroupTerm}
        customerId={customerFilter}
        siteGroupId={siteGroupFilter}
        onCustomerChange={handleScopeCustomerChange}
        onSiteGroupChange={handleScopeSiteGroupChange}
      />
      {!isMobile && (
        <ReturnStatCards
          summary={summary}
          summaryLoading={summaryLoading}
          selectedBucket={activeBucket}
          onBucketSelect={handleBucketSelect}
        />
      )}
    </>
  ) : undefined;

  const toolbarActions = isCustomer ? (
    <MuiLink
      href={`${API_DOMAIN}/static/rma/xlsx/odl_rma_form.xlsx`}
      target="_blank"
      rel="noreferrer"
      underline="hover"
      variant="body2"
    >
      {t('rma.downloadForm', 'Download RMA Form')}
    </MuiLink>
  ) : undefined;

  const renderMobileCard = useCallback(
    row => (
      <MobileSummaryCell
        row={row}
        linkTo={getViewUrl(row.id)}
        showSiteGroup={!isCustomer}
        customerTerm={customerTerm}
        siteGroupTerm={siteGroupTerm}
        t={t}
      />
    ),
    [isCustomer, getViewUrl, customerTerm, siteGroupTerm, t],
  );

  return (
    <>
      <EntityGrid
        basePath="rmas"
        fieldOverrides={fieldOverrides}
        extraParams={extraParams}
        options={{
          searchPlaceholder: t('general.searchRMAs', 'Search RMAs'),
          searchSlot: scopeSearch,
          persistKey,
          infiniteScroll: true,
          countNoun: t('rma.rmas', 'RMAs'),
          gridApiRef: apiRef,
          onGridReady: api => {
            captureGridReady(api);
            setGridReady(true);
          },
          extraColumns: [actionsColumn],
          toolbarContent,
          toolbarActions,
          statsHeader,
          selection:
            !isCustomer && isStaff ? { enabled: true, actions: selectionActions, persistSelection: true } : undefined,
          createAction:
            !isCustomer && isStaff
              ? {
                  label: t('rma.newRma', 'New RMA'),
                  // Context-first, like the classic RMA app: the new RMA is
                  // created inside the picked customer/group, so the pickers
                  // must be set before the form opens.
                  onClick: () => {
                    if (!customerFilter || !siteGroupFilter) {
                      dispatch(
                        addToast({
                          title: t('rma.newRma', 'New RMA'),
                          message: t(
                            'rma.newRmaNeedsContext',
                            'Pick a {{customer}} and {{group}} above first — the RMA is created in that context.',
                            { customer: customerTerm, group: siteGroupTerm },
                          ),
                          level: 'info',
                          dismissible: true,
                        }),
                      );
                      return;
                    }
                    navigate('/rma/new', {
                      state: {
                        rmaContext: {
                          customer: { id: customerFilter, name: scopeLabels.customer },
                          siteGroup: { id: siteGroupFilter, name: scopeLabels.site_group },
                        },
                      },
                    });
                  },
                }
              : undefined,
          mobile: { renderCard: renderMobileCard },
          columnVisibility,
        }}
      />
      <Dialog open={bulkInvoice != null} onClose={() => setBulkInvoice(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('rma.invoiceSelected', 'Invoice Selected')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: bulkInvoice?.blocked?.length ? 1.5 : 0 }}>
            {t('rma.bulkInvoiceConfirm', '{{count}} RMAs are ready to invoice.', {
              count: bulkInvoice?.okIds?.length ?? 0,
            })}
          </Typography>
          {Boolean(bulkInvoice?.blocked?.length) && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('rma.bulkInvoiceBlocked', '{{count}} cannot be invoiced and will be skipped:', {
                  count: bulkInvoice.blocked.length,
                })}
              </Typography>
              {bulkInvoice.blocked.map(entry => (
                <Typography key={entry.id} variant="caption" color="text.secondary" component="div">
                  {entry.detail}
                </Typography>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkInvoice(null)} color="inherit">
            {t('actions.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkInvoiceConfirm}
            disabled={bulkInvoiceRunning || !bulkInvoice?.okIds?.length}
          >
            {t('rma.invoice', 'Invoice')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
